import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import { ShoppingCart, Security, Delete, Check, Person, AddShoppingCart } from '@mui/icons-material';
import { OfferDTO } from '../../../user/on-sale/types';
import { formatMoney, moneyFromMinor } from '../../../utils/UnitConversion.utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { useCurrency } from '../../../../hooks/useCurrency';

interface Props {
  offer: OfferDTO;
  onAddToCart: () => void;
  onRemoveFromCart: () => void;
  onBuyNow: () => void;
  cartLoading?: boolean;
  buyLoading?: boolean;
  isAddedToCart: boolean;
  isBought: boolean;
}

const CheckOut: React.FC<Props> = ({
                                     offer,
                                     onAddToCart,
                                     onRemoveFromCart,
                                     onBuyNow,
                                     cartLoading = false,
                                     buyLoading = false,
                                     isAddedToCart,
                                     isBought
                                   }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);
  const { convertFromUSD } = useCurrency();

  const renderCartButtonContent = () => {
    if (cartLoading) return <CircularProgress size={20} color="inherit" />;

    if (isAddedToCart) {
      return (
          <div className="flex items-center gap-2">
            <Delete fontSize="small" />
            <span>Remove</span>
          </div>
      );
    }

    return (
        <div className="flex items-center gap-2">
          <AddShoppingCart fontSize="small" />
          <span>Add to Cart</span>
        </div>
    );
  };

  return (
      <div className="bg-neutral-100 dark:bg-black/20 border border-black/5 dark:border-white/5 rounded p-4 flex flex-col gap-4 mt-auto">
        <div className="flex flex-col">
          <span className="text-[10px] text-neutral-500 dark:text-gray-500 uppercase font-black tracking-wider">
            Price
          </span>
          <div className="flex items-baseline gap-1">
            <span className="font-bold text-dark-primary">{currencySymbol}</span>
            <span className="text-2xl font-black text-neutral-900 dark:text-white">
              {formatMoney(convertFromUSD(moneyFromMinor(offer.priceMinor)))}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {!isAuthenticated ? (
              <Button
                  fullWidth
                  variant="contained"
                  disabled
                  className="!py-3 !text-sm uppercase tracking-tight"
              >
                Login to Buy
              </Button>
          ) : isBought ? (
              <Button
                  fullWidth
                  variant="contained"
                  disabled
                  className="!py-3 !text-sm uppercase tracking-tight !bg-green-500/20 !text-green-500 border border-green-500/30"
              >
                <Check fontSize="small" className="mr-2" />
                Bought
              </Button>
          ) : offer.isItemOwner ? (
              <Button
                  fullWidth
                  variant="contained"
                  disabled
                  className="!py-3 !text-sm uppercase tracking-tight"
              >
                <Person fontSize="small" className="mr-2" />
                Your Offer
              </Button>
          ) : (
              <>
                <Button
                    variant="contained"
                    fullWidth
                    disabled={cartLoading || buyLoading}
                    onClick={isAddedToCart ? onRemoveFromCart : onAddToCart}
                    className={`!py-3 !text-sm !shadow-lg uppercase tracking-tight transition-colors ${
                        isAddedToCart
                            ? '!bg-neutral-600 hover:!bg-red-700 !text-white'
                            : '!bg-dark-primary hover:!bg-dark-stronger'
                    }`}
                >
                  {renderCartButtonContent()}
                </Button>

                <Button
                    variant="contained"
                    fullWidth
                    disabled={buyLoading || cartLoading}
                    onClick={onBuyNow}
                    className="!py-3 !text-sm !shadow-lg uppercase tracking-tight !bg-dark-primary hover:!bg-dark-stronger"
                >
                  {buyLoading ? (
                      <CircularProgress size={20} color="inherit" />
                  ) : (
                      'Buy Now'
                  )}
                </Button>
              </>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 text-[9px] text-neutral-400 dark:text-gray-500 uppercase font-black pt-3 border-t border-neutral-200 dark:border-white/5">
          <Security sx={{ fontSize: 12 }} />
          <span>Secure Transaction</span>
        </div>
      </div>
  );
};

export default CheckOut;
